import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Icon } from '../ui';
import AiMarkdown from '../ai/AiMarkdown';
import { timeAgo } from '../../lib/comms';
import {
  invokeTrelloBoard,
  type TrelloCardDetail,
  type TrelloComment,
} from '../../lib/trelloBoard';

interface TrelloCardDetailBodyProps {
  /** Trello card id or shortLink. Re-fetches whenever it changes. */
  cardId: string;
}

const blockStyle: React.CSSProperties = {
  background: 'var(--surface-raised)',
  border: '1px solid var(--border-hairline)',
  borderRadius: 'var(--radius-md)',
  padding: '12px 14px',
};

const emptyStyle: React.CSSProperties = {
  fontSize: 'var(--text-sm)',
  color: 'var(--text-faint)',
};

// timeAgo drops the year past a week; comment history can run back years
function commentDate(iso: string): string {
  const then = new Date(iso);
  if (Date.now() - then.getTime() < 7 * 24 * 60 * 60 * 1000) return timeAgo(iso);
  const sameYear = then.getFullYear() === new Date().getFullYear();
  return then.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/* ---------- collapsible section ---------- */

type SectionKey = 'description' | 'attachments' | 'comments';

// Collapsed state is a per-user preference: a card opens the way you last
// left its sections, wherever it opens.
const COLLAPSE_KEY = 'cockpit.trelloCard.collapsed';

function readCollapsed(): Partial<Record<SectionKey, boolean>> {
  try {
    const raw = localStorage.getItem(COLLAPSE_KEY);
    return raw ? (JSON.parse(raw) as Partial<Record<SectionKey, boolean>>) : {};
  } catch {
    return {};
  }
}

function writeCollapsed(id: SectionKey, collapsed: boolean) {
  try {
    localStorage.setItem(COLLAPSE_KEY, JSON.stringify({ ...readCollapsed(), [id]: collapsed }));
  } catch {
    // localStorage unavailable — the preference simply won't persist
  }
}

function CollapsibleBlock({
  id,
  title,
  count,
  children,
}: {
  id: SectionKey;
  title: string;
  count?: number;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed()[id] === true);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    writeCollapsed(id, next);
  };

  return (
    <div style={blockStyle}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!collapsed}
        className="pressable"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          width: '100%',
          margin: collapsed ? 0 : '0 0 8px',
          padding: 0,
          border: 'none',
          background: 'transparent',
          color: 'inherit',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: 'inherit',
        }}
      >
        <span className="t-eyebrow">
          {title}
          {count ? ` (${count})` : ''}
        </span>
        <Icon
          name="chevron-down"
          size={14}
          style={{
            flexShrink: 0,
            color: 'var(--text-faint)',
            transform: collapsed ? 'rotate(-90deg)' : 'none',
            transition: 'transform var(--dur-base) var(--ease-out)',
          }}
        />
      </button>
      {collapsed ? null : children}
    </div>
  );
}

/**
 * The live Trello state of one card — description, attachments (name + link)
 * and the full comment history — pulled through the trello-board proxy.
 * Every card detail screen in Client Comms renders this, so the sections and
 * their empty states stay identical wherever a card opens.
 */
export default function TrelloCardDetailBody({ cardId }: TrelloCardDetailBodyProps) {
  const [detail, setDetail] = useState<{ card: TrelloCardDetail; comments: TrelloComment[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setFailed(false);
    setLoading(true);
    invokeTrelloBoard<{ card: TrelloCardDetail; comments: TrelloComment[] }>({ action: 'card', cardId })
      .then((data) => {
        if (cancelled) return;
        setDetail({ card: data.card, comments: data.comments || [] });
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error loading Trello card:', err);
        setFailed(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cardId]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
        <div
          className="animate-spin w-6 h-6 border-2 rounded-full"
          style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  if (failed || !detail) {
    return (
      <div style={blockStyle}>
        <div className="t-eyebrow" style={{ marginBottom: '8px' }}>Trello card</div>
        <div style={emptyStyle}>
          Could not load this card from Trello — it may have been archived or deleted.
        </div>
      </div>
    );
  }

  const { card, comments } = detail;
  const attachments = card.attachments || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <CollapsibleBlock id="description" title="Description">
        {card.desc && card.desc.trim() ? (
          <AiMarkdown content={card.desc} />
        ) : (
          <div style={emptyStyle}>No description</div>
        )}
      </CollapsibleBlock>

      <CollapsibleBlock id="attachments" title="Attachments" count={attachments.length}>
        {attachments.length === 0 ? (
          <div style={emptyStyle}>No attachments</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {attachments.map((att) => (
              <a
                key={att.id}
                href={att.url}
                target="_blank"
                rel="noopener noreferrer"
                title={att.url}
                className="hover-underline"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                  minWidth: 0,
                  fontSize: 'var(--text-sm)',
                  color: 'var(--accent-text)',
                  textDecoration: 'none',
                }}
              >
                <Icon name="paperclip" size={13} style={{ flexShrink: 0 }} />
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {att.name || att.url}
                </span>
                <Icon name="external-link" size={12} style={{ flexShrink: 0, color: 'var(--text-faint)' }} />
              </a>
            ))}
          </div>
        )}
      </CollapsibleBlock>

      <CollapsibleBlock id="comments" title="Comments" count={comments.length}>
        {comments.length === 0 ? (
          <div style={emptyStyle}>No comments</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {comments.map((comment) => (
              <div
                key={comment.id}
                style={{
                  background: 'var(--surface-card)',
                  border: '1px solid var(--border-hairline)',
                  borderRadius: 'var(--radius-md)',
                  padding: '8px 10px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    gap: '8px',
                    marginBottom: '4px',
                    fontSize: 'var(--text-xs)',
                    color: 'var(--text-faint)',
                  }}
                >
                  <span
                    style={{
                      fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                      color: 'var(--text-muted)',
                    }}
                  >
                    {comment.memberCreator?.fullName || 'Unknown'}
                  </span>
                  <span className="t-num" title={new Date(comment.date).toLocaleString()}>
                    {commentDate(comment.date)}
                  </span>
                </div>
                {comment.data.text && comment.data.text.trim() ? (
                  <AiMarkdown content={comment.data.text} />
                ) : (
                  <div style={emptyStyle}>(empty comment)</div>
                )}
              </div>
            ))}
          </div>
        )}
      </CollapsibleBlock>
    </div>
  );
}
